import { Reveal } from "@/components/motion/Reveal";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import { SERVICE_PRICING, formatUsd } from "@/lib/service-pricing";
import { MagneticButton } from "@/components/motion/MagneticButton";

interface Copy {
  eyebrow: string;
  headline: string;
  sub: string;
  retainerLabel: string;
  retainerCaption: string;
  perMeetingLabel: string;
  perMeetingCaption: string;
  includes: string[];
  freeFirstMeeting: string;
  cta: string;
  secondary?: string;
  onSecondary?: () => void;
}

const DEFAULT_COPY: Copy = {
  eyebrow: "PRICING",
  headline: "Retainer + qualified meetings. No long-term contract.",
  sub: "One monthly retainer covers the team and the infrastructure. You only add per-meeting cost when we actually deliver one.",
  retainerLabel: "per month",
  retainerCaption: "Managed operations: your AM, deliverability team, tooling, warmed inboxes.",
  perMeetingLabel: "per qualified meeting",
  perMeetingCaption: "Only charged when a meeting is booked, held, and matches your ICP.",
  includes: [
    "Dedicated account manager",
    "Warmed inbox pool + domain infra",
    "ICP research + copy authoring",
    "Reply handling on your behalf",
    "Weekly performance reports",
    "Client portal access",
  ],
  freeFirstMeeting: "First qualified meeting is free — see the service before you commit.",
  cta: "Book a strategy call",
};

export function ServicePricingCard({
  copy = DEFAULT_COPY,
  onPrimary,
}: {
  copy?: Copy;
  onPrimary?: () => void;
}) {
  const primaryHandler =
    onPrimary ??
    (() => {
      window.location.assign(SERVICE_PRICING.strategyCallUrl);
    });

  return (
    <section className="relative">
      <div className="max-w-4xl mx-auto px-4 py-20 md:py-28">
        <Reveal stagger={60} className="text-center space-y-3 max-w-2xl mx-auto mb-10">
          <p className="text-[10px] font-mono uppercase tracking-widest text-primary">
            {copy.eyebrow}
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">
            {copy.headline}
          </h2>
          <p className="text-sm md:text-base text-foreground/80">{copy.sub}</p>
        </Reveal>

        <Reveal delay={200}>
          <div className="rounded-3xl border border-primary/30 bg-gradient-to-b from-card/70 to-card/40 backdrop-blur-xl p-6 md:p-10 shadow-2xl shadow-primary/10 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-border/60 bg-background/60 p-6 space-y-3">
                <p className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                  Managed retainer
                </p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4xl md:text-5xl font-bold text-foreground tabular-nums">
                    {formatUsd(SERVICE_PRICING.retainerMonthly)}
                  </span>
                  <span className="text-sm text-foreground/60">/ {copy.retainerLabel}</span>
                </div>
                <p className="text-sm text-foreground/75 leading-relaxed">
                  {copy.retainerCaption}
                </p>
              </div>

              <div className="rounded-2xl border border-primary/40 bg-primary/[0.06] p-6 space-y-3">
                <p className="text-[10px] font-mono uppercase tracking-widest text-primary">
                  Per meeting delivered
                </p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4xl md:text-5xl font-bold text-foreground tabular-nums">
                    {formatUsd(SERVICE_PRICING.perQualifiedMeeting)}
                  </span>
                  <span className="text-sm text-foreground/60">/ {copy.perMeetingLabel}</span>
                </div>
                <p className="text-sm text-foreground/75 leading-relaxed">
                  {copy.perMeetingCaption}
                </p>
              </div>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {copy.includes.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 text-sm text-foreground/85"
                >
                  <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {SERVICE_PRICING.freeFirstMeeting && (
              <div className="rounded-xl border border-success/30 bg-success/[0.06] px-4 py-3 text-center">
                <p className="text-sm text-foreground/90">
                  <span className="font-semibold text-success">Free first meeting.</span>{" "}
                  {copy.freeFirstMeeting}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <MagneticButton strength={5}>
                <Button
                  size="lg"
                  className="text-sm px-8 gap-2 glow-primary h-12"
                  onClick={primaryHandler}
                >
                  {copy.cta} <ArrowRight className="h-4 w-4" />
                </Button>
              </MagneticButton>
              {copy.secondary && copy.onSecondary && (
                <Button
                  size="lg"
                  variant="outline"
                  className="text-sm px-6 h-12"
                  onClick={copy.onSecondary}
                >
                  {copy.secondary}
                </Button>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
